/**
 * Cost Tracker
 * Monitors and tracks LLM API costs
 */
/**
 * Model pricing (cost per 1M tokens in USD)
 * Updated as of 2026-02-23
 */
export const MODEL_PRICING = {
    // DeepSeek
    'deepseek-chat': { input: 0.14, output: 0.28 },
    'deepseek-coder': { input: 0.14, output: 0.28 },
    // OpenAI
    'gpt-4-turbo': { input: 10, output: 30 },
    'gpt-4': { input: 30, output: 60 },
    'gpt-3.5-turbo': { input: 0.50, output: 1.50 },
    'gpt-4o': { input: 2.50, output: 10 },
    // Anthropic
    'claude-3-opus': { input: 15, output: 75 },
    'claude-3-sonnet': { input: 3, output: 15 },
    'claude-3-haiku': { input: 0.25, output: 1.25 },
    // Google
    'gemini-1.5-pro': { input: 1.25, output: 5.00 },
    'gemini-1.5-flash': { input: 0.075, output: 0.30 },
    'gemini-2.0-flash': { input: 0.075, output: 0.30 },
};
/**
 * Cost Tracker Service
 */
export class CostTracker {
    entries = [];
    STORAGE_KEY = 'ai_cost_tracker';
    MAX_ENTRIES = 10000;
    // Alert thresholds (in USD)
    dailyLimit = 100;
    hourlyLimit = 10;
    requestLimit = 1;
    constructor(options) {
        if (options) {
            if (options.dailyLimit)
                this.dailyLimit = options.dailyLimit;
            if (options.hourlyLimit)
                this.hourlyLimit = options.hourlyLimit;
            if (options.requestLimit)
                this.requestLimit = options.requestLimit;
        }
        this.loadFromStorage();
    }
    /**
     * Calculate cost for a request
     */
    calculateCost(model, inputTokens, outputTokens) {
        const pricing = this.getModelPricing(model);
        if (!pricing) {
            console.warn(`[CostTracker] Unknown model pricing: ${model}`);
            return 0;
        }
        const inputCost = (inputTokens / 1_000_000) * pricing.input;
        const outputCost = (outputTokens / 1_000_000) * pricing.output;
        return inputCost + outputCost;
    }
    /**
     * Get pricing for a model (handles partial matches)
     */
    getModelPricing(model) {
        // Exact match
        if (model in MODEL_PRICING) {
            return MODEL_PRICING[model];
        }
        // Partial match (e.g., "openai/gpt-4-turbo" matches "gpt-4-turbo")
        for (const [key, pricing] of Object.entries(MODEL_PRICING)) {
            if (model.includes(key)) {
                return pricing;
            }
        }
        return null;
    }
    /**
     * Log a request
     */
    logRequest(model, inputTokens, outputTokens, metadata) {
        const cost = this.calculateCost(model, inputTokens, outputTokens);
        const entry = {
            model,
            inputTokens,
            outputTokens,
            cost,
            timestamp: Date.now(),
            metadata,
        };
        this.entries.push(entry);
        // Limit entries to prevent memory bloat
        if (this.entries.length > this.MAX_ENTRIES) {
            this.entries = this.entries.slice(-this.MAX_ENTRIES);
        }
        this.persist();
        return entry;
    }
    /**
     * Get today's cost
     */
    getTodaysCost() {
        const todayStart = new Date().setHours(0, 0, 0, 0);
        return this.entries
            .filter(e => e.timestamp >= todayStart)
            .reduce((sum, e) => sum + e.cost, 0);
    }
    /**
     * Get last hour's cost
     */
    getLastHourCost() {
        const oneHourAgo = Date.now() - 60 * 60 * 1000;
        return this.entries
            .filter(e => e.timestamp >= oneHourAgo)
            .reduce((sum, e) => sum + e.cost, 0);
    }
    /**
     * Get cost for a date range
     */
    getCostForRange(startDate, endDate) {
        const start = startDate.getTime();
        const end = endDate.getTime();
        return this.entries
            .filter(e => e.timestamp >= start && e.timestamp <= end)
            .reduce((sum, e) => sum + e.cost, 0);
    }
    /**
     * Get daily cost summary
     */
    getDailySummary(date = new Date()) {
        const dayStart = new Date(date).setHours(0, 0, 0, 0);
        const dayEnd = new Date(date).setHours(23, 59, 59, 999);
        const dayEntries = this.entries.filter(e => e.timestamp >= dayStart && e.timestamp <= dayEnd);
        const modelBreakdown = {};
        let totalCost = 0;
        let totalTokens = 0;
        for (const entry of dayEntries) {
            totalCost += entry.cost;
            totalTokens += entry.inputTokens + entry.outputTokens;
            if (!modelBreakdown[entry.model]) {
                modelBreakdown[entry.model] = { cost: 0, requests: 0 };
            }
            modelBreakdown[entry.model].cost += entry.cost;
            modelBreakdown[entry.model].requests += 1;
        }
        return {
            date: date.toISOString().split('T')[0],
            totalCost,
            totalTokens,
            requestCount: dayEntries.length,
            modelBreakdown,
        };
    }
    /**
     * Check if cost limits are exceeded
     */
    checkAlerts() {
        const alerts = [];
        // Daily limit check
        const dailyCost = this.getTodaysCost();
        if (dailyCost >= this.dailyLimit) {
            alerts.push({
                type: 'daily',
                threshold: this.dailyLimit,
                current: dailyCost,
                exceeded: true,
                message: `Daily cost limit exceeded: $${dailyCost.toFixed(2)} / $${this.dailyLimit}`,
            });
        }
        // Hourly limit check
        const hourlyCost = this.getLastHourCost();
        if (hourlyCost >= this.hourlyLimit) {
            alerts.push({
                type: 'hourly',
                threshold: this.hourlyLimit,
                current: hourlyCost,
                exceeded: true,
                message: `Hourly cost limit exceeded: $${hourlyCost.toFixed(2)} / $${this.hourlyLimit}`,
            });
        }
        return alerts;
    }
    /**
     * Can make request without exceeding limits
     */
    canMakeRequest(estimatedCost) {
        // Check request limit
        if (estimatedCost > this.requestLimit) {
            return {
                allowed: false,
                reason: `Request cost ($${estimatedCost.toFixed(4)}) exceeds per-request limit ($${this.requestLimit})`,
            };
        }
        // Check daily limit
        const dailyCost = this.getTodaysCost();
        if (dailyCost + estimatedCost > this.dailyLimit) {
            return {
                allowed: false,
                reason: `Daily cost limit would be exceeded: $${(dailyCost + estimatedCost).toFixed(2)} / $${this.dailyLimit}`,
            };
        }
        // Check hourly limit
        const hourlyCost = this.getLastHourCost();
        if (hourlyCost + estimatedCost > this.hourlyLimit) {
            return {
                allowed: false,
                reason: `Hourly cost limit would be exceeded: $${(hourlyCost + estimatedCost).toFixed(2)} / $${this.hourlyLimit}`,
            };
        }
        return { allowed: true };
    }
    /**
     * Get all entries
     */
    getEntries() {
        return [...this.entries];
    }
    /**
     * Get entries for a specific model
     */
    getEntriesForModel(model) {
        return this.entries.filter(e => e.model.includes(model));
    }
    /**
     * Clear all entries
     */
    clear() {
        this.entries = [];
        this.persist();
    }
    /**
     * Export to CSV
     */
    exportToCSV() {
        const header = 'Timestamp,Model,Input Tokens,Output Tokens,Cost USD\n';
        const rows = this.entries.map(e => {
            const date = new Date(e.timestamp).toISOString();
            return `${date},${e.model},${e.inputTokens},${e.outputTokens},${e.cost.toFixed(6)}`;
        });
        return header + rows.join('\n');
    }
    /**
     * Persist to storage
     */
    persist() {
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.entries));
            }
        }
        catch (err) {
            console.error('[CostTracker] Failed to persist:', err);
        }
    }
    /**
     * Load from storage
     */
    loadFromStorage() {
        try {
            if (typeof localStorage !== 'undefined') {
                const stored = localStorage.getItem(this.STORAGE_KEY);
                if (stored) {
                    this.entries = JSON.parse(stored);
                }
            }
        }
        catch (err) {
            console.error('[CostTracker] Failed to load from storage:', err);
            this.entries = [];
        }
    }
}
/**
 * Global cost tracker instance
 */
export const costTracker = new CostTracker();
//# sourceMappingURL=costTracker.js.map